/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { getRewardText } from "@lemmonhq/orders";
import type { CoinOffer } from "@app/server/client";
import { CoinStack } from "./index";

export type CoinClaimModalProps = {
  offers: CoinOffer[];
  shopName?: string;
  /** True while the claim request is in flight. */
  claiming?: boolean;
  /** A refusal or failure message, already translated. */
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Confirms adding a receipt's coins to the signed-in account.
 *
 * A confirmation rather than a silent claim, because claiming is irreversible
 * in the direction that matters: the unique index on
 * `(payment_id, coin_program_id, type)` means a receipt can only ever be
 * claimed once, by one account. Someone signed in on a shared phone, or holding
 * a receipt they mean to hand to a colleague, has exactly one chance to not do
 * that — so the account being credited is named here.
 */
export const CoinClaimModal = defineComponent<CoinClaimModalProps>({
  name: "CoinClaimModal",
  props: ["offers", "shopName", "claiming", "error", "onConfirm", "onClose"],
  setup(props) {
    const { t } = useLocalization();

    const total = computed(() =>
      props.offers.reduce((sum, offer) => sum + offer.amount, 0),
    );

    return () => (
      <LemmonModal
        title={t("customer.coins.confirm_title")}
        // Losing the claim to a stray backdrop tap is recoverable, but a
        // half-finished request is not — hold the modal open while it is in
        // flight so the result has somewhere to land.
        disableCloseByClickingBackdrop={props.claiming}
        hideClose={props.claiming}
        onClose={() => {
          if (!props.claiming) props.onClose();
        }}
        renderButtons={() => (
          <div class="flex gap-2 p-3 sm:p-4 border-t border-base-300">
            <button
              type="button"
              class="btn btn-ghost flex-1"
              disabled={props.claiming}
              onClick={() => props.onClose()}
            >
              {t("customer.coins.confirm_cancel")}
            </button>
            <button
              type="button"
              class="btn btn-primary flex-1 gap-2"
              disabled={props.claiming}
              onClick={() => props.onConfirm()}
            >
              {props.claiming ? (
                <span class="loading loading-spinner loading-sm" />
              ) : (
                <span class="material-symbols-outlined text-lg">redeem</span>
              )}
              {t("customer.coins.confirm_cta")}
            </button>
          </div>
        )}
      >
        <div class="p-5 sm:p-6 space-y-5">
          <div>
            <p class="text-3xl font-bold text-base-content leading-tight">
              {t("customer.coins.offer_headline", [total.value])}
            </p>
            {props.shopName && (
              <p class="text-sm text-base-content/60 mt-1">
                {t("customer.coins.at_shop", [props.shopName])}
              </p>
            )}
          </div>

          <div class="space-y-3">
            {props.offers.map((offer) => {
              const tier = (offer.tiers ?? [])[0];
              return (
                <div
                  key={offer.coin_program_id}
                  class="rounded-xl border border-base-300 p-4 space-y-2"
                >
                  <p class="text-sm font-medium text-base-content/70">
                    {offer.name}
                  </p>
                  <div class="flex items-center gap-3 flex-wrap">
                    <CoinStack offer={offer} />
                    <span class="text-sm text-base-content/50">
                      {offer.amount} × {offer.coin_label}
                    </span>
                  </div>
                  {tier && (
                    <p class="text-sm text-base-content/70">
                      <span class="font-medium text-primary">
                        {getRewardText(tier.reward, t)}
                      </span>
                      <span class="text-base-content/50">
                        {" · "}
                        {t("customer.coins.tier_cost", [
                          tier.amount,
                          offer.coin_label,
                        ])}
                      </span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <p class="text-sm text-base-content/60">
            {t("customer.coins.confirm_body")}
          </p>

          {/* Refusals are specific by design: a generic failure reads as theft
              to someone holding a receipt that promised them coins. */}
          {props.error && (
            <div class="flex items-start gap-2 rounded-lg bg-error/10 p-3">
              <span class="material-symbols-outlined text-base text-error shrink-0">
                error
              </span>
              <p class="text-sm text-error">{props.error}</p>
            </div>
          )}
        </div>
      </LemmonModal>
    );
  },
});
